/**
 * Tiny safe calculator for the Tool Use demo (Story 305).
 *
 * Per challenger note: pulling in `mathjs` (~600 kB) for a demo is not
 * acceptable. We hand-roll a recursive-descent parser that supports the
 * four basic operators, parentheses, unary minus, and decimal numbers. The
 * grammar is:
 *
 *   expr   = term (('+' | '-') term)*
 *   term   = factor (('*' | '/') factor)*
 *   factor = NUMBER | '(' expr ')' | '-' factor
 *
 * `evaluate('2 + 2')` → 4. Throws on syntax errors with a useful message.
 * No reference to `eval` / `Function`; safe to feed model-supplied input.
 */

interface Token {
  kind: 'number' | 'op' | 'lparen' | 'rparen';
  value: string;
}

function tokenise(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const ch = source[i] as string;
    if (ch === ' ' || ch === '\t') {
      i += 1;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < source.length && /[0-9.]/.test(source[j] as string)) j += 1;
      const slice = source.slice(i, j);
      if ((slice.match(/\./g) || []).length > 1) {
        throw new Error(`Invalid number literal: ${slice}`);
      }
      tokens.push({ kind: 'number', value: slice });
      i = j;
      continue;
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }
    if (ch === '(') {
      tokens.push({ kind: 'lparen', value: ch });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ kind: 'rparen', value: ch });
      i += 1;
      continue;
    }
    throw new Error(`Unexpected character: ${ch}`);
  }
  return tokens;
}

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  parse(): number {
    const value = this.expr();
    if (this.pos < this.tokens.length) {
      throw new Error(`Unexpected token at position ${this.pos}`);
    }
    return value;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    const t = this.tokens[this.pos];
    if (!t) throw new Error('Unexpected end of input');
    this.pos += 1;
    return t;
  }

  private expr(): number {
    let v = this.term();
    while (this.peek()?.kind === 'op' && (this.peek()?.value === '+' || this.peek()?.value === '-')) {
      const op = this.consume();
      const rhs = this.term();
      v = op.value === '+' ? v + rhs : v - rhs;
    }
    return v;
  }

  private term(): number {
    let v = this.factor();
    while (this.peek()?.kind === 'op' && (this.peek()?.value === '*' || this.peek()?.value === '/')) {
      const op = this.consume();
      const rhs = this.factor();
      if (op.value === '/' && rhs === 0) throw new Error('Division by zero');
      v = op.value === '*' ? v * rhs : v / rhs;
    }
    return v;
  }

  private factor(): number {
    const t = this.peek();
    if (!t) throw new Error('Unexpected end of input');
    if (t.kind === 'op' && t.value === '-') {
      this.consume();
      return -this.factor();
    }
    if (t.kind === 'number') {
      this.consume();
      const n = Number(t.value);
      if (!Number.isFinite(n)) throw new Error(`Invalid number: ${t.value}`);
      return n;
    }
    if (t.kind === 'lparen') {
      this.consume();
      const v = this.expr();
      const close = this.consume();
      if (close.kind !== 'rparen') throw new Error('Expected closing parenthesis');
      return v;
    }
    throw new Error(`Unexpected token: ${t.value}`);
  }
}

export function evaluate(expression: string): number {
  if (typeof expression !== 'string' || expression.trim().length === 0) {
    throw new Error('Empty expression');
  }
  const parser = new Parser(tokenise(expression));
  return parser.parse();
}
